// Models
import { IExerciseData, IExerciseState } from 'storage/exercise/models'
import {
  IFormInputs,
  IModelInput,
  IWorkoutSetInput,
  TFormValues,
  TMuscleGroupFilter,
  TSetSpecialSet,
  TSpecialSetInput,
} from 'models'
import IStore from 'lib/redux/models'

// React
import {
  FC,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react'

// Libraries
import { FormProvider, UseFormReturn } from 'react-hook-form'
import { ThemeContext } from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { generatePath, useNavigate, useParams } from 'react-router-dom'

// Misc
import { buttonClickTracking } from 'utils/tracking'
import { prepareModelsToRedux } from 'filters/workouts'
import { success } from 'storage/workoutModel/duck'
import { uid } from 'utils/functions'
import { urls } from 'routes/paths'
import { useMenu, useModal } from 'hooks'

// Components
import * as Styled from './styled'
import { CommonList, Icon } from 'heeds-ds'
import {
  ExerciseItem,
  LoaderExerciseItem,
  ModalExerciseImage,
  ModalMuscleGroups,
  WorkoutSets,
} from 'components'
import ExerciseSearchBar from 'blocks/dashboard/workout/ExerciseSearchBar'

// Constants
const EMPTY_SPECIAL_SERIE: TSpecialSetInput = {
  id: '',
  name: '',
  type: 'super_set',
  interval: '',
  quantity: '',
  exercises: {},
  comments: '',
}

type Props = {
  addWorkoutSet: (modelId: string, workoutSet: IWorkoutSetInput) => void
  changePage: (page: string) => void
  filters: TMuscleGroupFilter[]
  formMethods: UseFormReturn<TSpecialSetInput, TFormValues>
  inputValue: string
  loadMoreExercises: () => void
  methods: UseFormReturn<IFormInputs, TFormValues>
  onChangeFilters: (filters: TMuscleGroupFilter[]) => void
  onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  removeExerciseFromWorkoutSet: (
    modelId: string,
    workoutSetId: string,
    exerciseIndex: number,
  ) => void
  removeFilter: (label: string) => void
  removeWorkoutSet: (modelId: string, workoutSetId: string) => void
  replicateWorkoutSetFields: (
    modelId: string,
    workoutSet: IWorkoutSetInput,
  ) => void
  selectedModelIndex: number
  setSpecialSet: TSetSpecialSet
  stateModels: IModelInput[]
}

const WorkoutModelsSets: FC<Props> = ({
  addWorkoutSet,
  changePage,
  filters,
  formMethods,
  inputValue,
  loadMoreExercises,
  methods,
  onChangeFilters,
  onSearchChange,
  removeFilter,
  selectedModelIndex,
  setSpecialSet,
  stateModels,
  ...restWorkoutSetsProps
}) => {
  const { id = '', routine_id = '' } = useParams()
  const { exercises } = useSelector<IStore, IExerciseState>(
    (state) => state.exercise,
  )
  const { setPagename } = useMenu()
  const { closeModal, isVisible, openModal } = useModal()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const theme = useContext(ThemeContext)

  const [selectedExercise, setSelectedExercise] = useState<
    IExerciseData | undefined
  >()

  const { handleSubmit } = methods

  const { setValue } = formMethods

  const onSubmit = useCallback(
    (formData: IFormInputs) => {
      const reduxData = prepareModelsToRedux(formData, stateModels)

      dispatch(success({ workoutModels: reduxData }))

      navigate(generatePath(urls.workoutRoutineReview, { id, routine_id }))
    },
    [dispatch, id, navigate, routine_id, stateModels],
  )

  const addNewWorkoutSet = useCallback(
    (exercise: IExerciseData) => {
      const modelId = stateModels[selectedModelIndex].id
      const setId = 'NEW' + uid()

      const newWorkoutSet: IWorkoutSetInput = {
        id: setId,
        type: 'normal',
        quantity: '',
        interval: '',
        exercises: [
          { id: uid(), cadence: '', exercise, repetitions: '', weight: '' },
        ],
      }
      addWorkoutSet(modelId, newWorkoutSet)
    },
    [addWorkoutSet, selectedModelIndex, stateModels],
  )

  const openExerciseGifModal = useCallback(
    (exercise: IExerciseData) => {
      setSelectedExercise(exercise)
      openModal('mobile-exercise-gif-modal')
    },
    [openModal],
  )

  const renderExercise = useCallback(
    (exercise: IExerciseData) => {
      return (
        <ExerciseItem
          exercise={exercise}
          key={exercise.id}
          margin="0 0 16px"
          onClick={() => addNewWorkoutSet(exercise)}
          onClickImage={openExerciseGifModal}
        />
      )
    },
    [addNewWorkoutSet, openExerciseGifModal],
  )

  const newSpecialSet = useMemo(() => {
    return {
      ...EMPTY_SPECIAL_SERIE,
      id: uid(),
      name: 'Série Especial',
    }
  }, [])

  const closeExerciseGifModal = () => {
    setSelectedExercise(undefined)
    closeModal()
  }

  useLayoutEffect(() => {
    setSpecialSet(undefined)
    setPagename(stateModels[selectedModelIndex].name)

    return () => setPagename('Dashboard')
  }, [selectedModelIndex, setPagename, setSpecialSet, stateModels])

  return (
    <Styled.Container>
      <Styled.AddSpecialSeriesButton
        onClick={() => {
          formMethods.reset({ ...newSpecialSet, exercises: {} })
          changePage('3')
          setSpecialSet({ ...newSpecialSet, exercises: {} })
        }}
        track={buttonClickTracking}
        trackName="navigate_to_mobile_special_workout_set_in_workout_create"
      >
        <Icon iconName="add" color={theme.colors.interactive.default} />
        Adicionar série especial
      </Styled.AddSpecialSeriesButton>

      <ExerciseSearchBar
        filters={filters}
        onClickFilter={() => openModal('muscle-group-modal')}
        onSearchChange={onSearchChange}
        removeFilter={removeFilter}
        searchQuery={inputValue}
      />

      <FormProvider {...methods}>
        <Styled.Form onSubmit={handleSubmit(onSubmit)}>
          <WorkoutSets
            {...restWorkoutSetsProps}
            changePage={changePage}
            formIndex={selectedModelIndex}
            onEditSpecialSet={({ id, quantity, interval, exercises }) => {
              setValue('id', id)
              setValue('quantity', quantity)
              setValue('interval', interval)
              setValue('exercises', exercises)
            }}
            openExerciseGifModal={openExerciseGifModal}
            selectedModelIndex={selectedModelIndex}
            setSpecialSet={setSpecialSet}
            stateModels={stateModels}
          />
        </Styled.Form>
      </FormProvider>

      <CommonList<IExerciseData>
        columns={['']}
        data={exercises?.results || []}
        hasMore={!!exercises?.next}
        loaderComponent={<LoaderExerciseItem />}
        onEndReached={loadMoreExercises}
        renderItem={renderExercise}
        scrollableDiv="dashboard-content"
        showHeader={false}
      />

      {isVisible === 'mobile-exercise-gif-modal' && (
        <ModalExerciseImage
          exercise={selectedExercise}
          handleClose={closeExerciseGifModal}
        />
      )}

      {isVisible === 'muscle-group-modal' && (
        <ModalMuscleGroups filters={filters} onChange={onChangeFilters} />
      )}
    </Styled.Container>
  )
}

export default WorkoutModelsSets
